import type { OptionalPageLimit } from './common';
import type { ApiResponse, GetListResponse } from './generic';

// ─────────────────────────────────────────────────────────────
// ENUMS & CONSTANTS (Matching TypeORM column structures)
// ─────────────────────────────────────────────────────────────

export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export type TaskScheduleType = 'one_time' | 'recurring';

export type TaskAssignmentMode = 'group' | 'individual';

export enum FlowTaskAction {
  Start = 'start',
  Submit = 'submit',
  Complete = 'complete',
  Uncomplete = 'uncomplete',
  Approve = 'approve',
  Reopen = 'reopen',
}

export type AssignmentStatus =
  | 'not_started'
  | 'in_progress'
  | 'submitted'
  | 'approved'
  | 'completed';

export type TaskAssigneeType = 'admin' | 'employee';

export type TaskSource = 'company' | 'flow';

/**
 * What a workflow step does (D68): a task for a person, or a step that sends
 * a document from E-signatures and completes itself once it is signed or
 * confirmed as read.
 */
export type FlowStepKind =
  | 'task'
  | 'send_for_signature'
  | 'send_for_acknowledgement'
  | 'send_message'
  | 'send_email';

/** Send steps: at once from the profile, or after HR reviews it. */
export type FlowStepSendMode = 'automatic' | 'review_first';

/** Where a send step stands. */
export type FlowStepStatus =
  | 'waiting'
  | 'review'
  | 'sent'
  | 'completed'
  | 'declined'
  | 'expired'
  | 'cancelled'
  | 'failed';

/** The document a send step sent (its latest send). */
export interface FlowStepDocument {
  signatureRequestId: string | null;
  broadcastId: string | null;
  acknowledgementId: string | null;
}

export const isSendStep = (kind: FlowStepKind | null | undefined) =>
  kind === 'send_for_signature' || kind === 'send_for_acknowledgement';
export const FLOW_COMMUNICATION_VARIABLES = [
  'firstName',
  'lastName',
  'fullName',
  'companyName',
  'startDate',
  'managerName',
] as const;
export type FlowCommunicationVariable =
  (typeof FLOW_COMMUNICATION_VARIABLES)[number];
export interface FlowStepCommunication {
  language: 'en' | 'fr';
  body: string;
  subject?: string;
  presetId?: 'custom' | 'install_kollabhr';
  emailRecipient?: 'account' | 'personal' | 'work';
}
export const isCommunicationStep = (kind: FlowStepKind | null | undefined) =>
  kind === 'send_message' || kind === 'send_email';
export const isAutomaticFlowStep = (kind: FlowStepKind | null | undefined) =>
  isSendStep(kind) || isCommunicationStep(kind);
export interface WorkflowCommunicationPreset {
  label: string;
  id: 'install_kollabhr';
  language: 'en' | 'fr';
  subject: string;
  body: string;
}
export interface WorkflowCommunicationCatalog {
  variables: readonly FlowCommunicationVariable[];
  presets: WorkflowCommunicationPreset[];
}
export interface WorkflowCommunicationPreview {
  subject?: string;
  html?: string;
  text: string;
  variables: Record<FlowCommunicationVariable, string>;
}

export type TaskFlowType = 'onboarding' | 'offboarding' | 'automation';
export type TaskFlowStatus = 'in_progress' | 'completed';

export type TaskSortBy =
  | 'recently_updated'
  | 'oldest_updated'
  | 'due_date_earliest'
  | 'due_date_latest';

export type RecurFrequency = 'daily' | 'weekly' | 'monthly';

export type RecurEndType = 'never' | 'after' | 'on';

export type ThreadType = 'group' | 'individual';

export type OccurrenceJobStatus =
  | 'scheduled'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type OccurrenceJobType = 'generate_next_occurrence';

export type CompanyTaskActivityType =
  | 'task_created'
  | 'task_updated'
  | 'occurrence_created'
  | 'status_changed'
  | 'submitted'
  | 'approved'
  | 'reopened'
  | 'assignee_added'
  | 'assignee_removed'
  | 'message_posted'
  | 'message_updated'
  | 'message_deleted';

// ─────────────────────────────────────────────────────────────
// RECURRENCE & CORE SCHEMAS
// ─────────────────────────────────────────────────────────────

export interface RecurrenceRuleDto {
  recurFrequency: RecurFrequency;
  recurInterval: number; // Repeat every N frequency units
  recurEndType: RecurEndType;
  recurWeekdays?: number[]; // 0=Sun .. 6=Sat (integer array matches backend JSONB)
  recurMonthDay?: number; // 1..31
  recurStartsOn: string; // YYYY-MM-DD
  recurEndCount?: number; // Required when recurEndType='after'
  recurEndOn?: string; // YYYY-MM-DD, required when recurEndType='on'
  recurDueOffset?: number; // Days after each occurrence's assign date to set due date
}

export interface TaskAssigneeInput {
  targetType: TaskAssigneeType;
  targetId: string; // CompanyAdmin ID or Employee ID
}

export interface CompanyTaskAttachmentInputDto {
  key: string; // S3 object key returned by upload endpoint
  fileName?: string;
  mimeType?: string;
  fileSize?: number;
}

// ─────────────────────────────────────────────────────────────
// SHARED FIELD MIXINS
// ─────────────────────────────────────────────────────────────

export interface TaskEntityBase {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  createdByUserId?: string;
}

export interface TaskFileFields {
  fileName?: string;
  fileUrl: string; // Holds S3 presigned URL on FE load
  fileSize?: string; // bigint representation from database
  mimeType?: string;
}

// ─────────────────────────────────────────────────────────────
// BASE ENTITY INTERFACES (Matching TypeORM entity schemas)
// ─────────────────────────────────────────────────────────────

export interface TaskUserSummary {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface TaskAttachment extends TaskFileFields {
  id: string;
  companyTaskId: string;
  key: string;
  uploadedByUserId?: string;
  uploadedAt: string; // timestamptz string
}

export interface TaskAssignee extends Pick<TaskEntityBase, 'id' | 'createdAt'> {
  companyTaskId: string;
  targetType: TaskAssigneeType;
  targetId: string;
  // Hydrated user details for UI rendering
  firstName: string;
  lastName: string;
  email: string;
  designation?: string;
  status: AssignmentStatus; // Synced with the active occurrence assignment status
}

export interface CompanyTaskOccurrence
  extends Pick<TaskEntityBase, 'id' | 'createdAt'> {
  companyTaskId: string;
  seq: number;
  assignDate: string; // YYYY-MM-DD
  dueDate?: string | null; // YYYY-MM-DD
  // Snapshot from the task at occurrence-creation time — the status engine
  // branches on THIS, not on the parent task's current requiresApproval.
  requiresApproval: boolean;
  // Snapshot of the thread shape this occurrence was fanned out with — decide
  // individual vs group from THIS, not the parent task's current assignmentMode,
  // so switching a task's mode can't strand an older occurrence's discussion.
  assignmentMode?: TaskAssignmentMode | null;
}

export interface CompanyTaskAssignment
  extends Pick<TaskEntityBase, 'id' | 'createdAt' | 'updatedAt'> {
  occurrenceId: string;
  assigneeUserId: string;
  role: TaskAssigneeType;
  status: AssignmentStatus;
  startedAt?: string | null;
  submittedAt?: string | null;
  completedAt?: string | null;
  approvedAt?: string | null;
  approvedBy?: string | null;
  adminNote?: string | null;
  assigneeNote?: string | null;
  occurrence?: CompanyTaskOccurrence;
  assigneeUser?: TaskUserSummary;
  submissionAttachments?: CompanyTaskSubmissionAttachment[];
}

export interface CompanyTaskSubmissionAttachment
  extends Pick<TaskEntityBase, 'id' | 'createdAt'>,
    TaskFileFields {
  assignmentId: string;
  uploadedByUserId?: string;
}

export interface CompanyTaskThread
  extends Pick<TaskEntityBase, 'id' | 'createdAt'> {
  occurrenceId: string;
  type: ThreadType;
  participantUserId?: string | null;
  lastMessageAt?: string | null;
  messages?: CompanyTaskMessage[];
}

export interface CompanyTaskMessage
  extends Pick<TaskEntityBase, 'id' | 'createdAt'> {
  threadId: string;
  senderUserId: string;
  body: string;
  editedAt?: string | null;
  senderUser?: TaskUserSummary;
  attachments: CompanyTaskMessageAttachment[];
}

export interface CompanyTaskMessageAttachment
  extends Pick<TaskEntityBase, 'id' | 'createdAt'>,
    TaskFileFields {
  messageId: string;
}

export interface CompanyTaskActivityLog
  extends Pick<TaskEntityBase, 'id' | 'createdAt' | 'createdByUserId'> {
  occurrenceId: string;
  logType: CompanyTaskActivityType;
  fromValue?: string | null;
  toValue?: string | null;
  metadata?: Record<string, unknown> | null;
  createdByUser?: TaskUserSummary;
}

export interface TaskAssigneeSummary extends TaskAssigneeInput {
  name: string;
}

// ─────────────────────────────────────────────────────────────
// CORE DOMAIN MODEL (Main Task Object)
// ─────────────────────────────────────────────────────────────

// Shared by TaskItem and MyAdminTaskDefinition — the fields that define
// "what the task is", independent of who it's assigned to or its schedule state.
export interface TaskDefinitionCore {
  companyId: string;
  title: string;
  description?: string | null;
  priority: TaskPriority;
  requiresApproval: boolean;
  scheduleType: TaskScheduleType;
  assignmentMode: TaskAssignmentMode;
  recurrenceRule?: RecurrenceRuleDto | null;
  attachments: TaskAttachment[];
}

export interface TaskOccurrenceSnapshot {
  occurrence: CompanyTaskOccurrence;
  assignments: CompanyTaskAssignment[];
}

export interface TaskItem extends TaskEntityBase, TaskDefinitionCore {
  assignToAllEmployees: boolean; // Dynamic dynamic roster fanning flag
  assignToAllAdmins: boolean; // Dynamic dynamic roster fanning flag
  assignDate?: string | null; // YYYY-MM-DD (Used if one_time)
  dueDate?: string | null; // YYYY-MM-DD (Used if one_time)
  assignees: TaskAssigneeSummary[];
  updatedByUserId?: string;
  assigneeCount: number;
  currentOccurrence: TaskOccurrenceSnapshot | null;

  // Relations mapped down to UI components
  occurrences: CompanyTaskOccurrence[];
  createdBy: TaskUserSummary | null;
}

export interface TaskDetailsData {
  task: TaskItem;

  // Branches the dynamic (all-employees / all-admins) fan-out is limited to.
  // Empty means the whole company.
  branchIds: string[];

  currentOccurrence: TaskOccurrenceSnapshot | null;
}

export type GetTaskByIdResponse = ApiResponse<TaskDetailsData>;
// ─────────────────────────────────────────────────────────────
// REQUEST PAYLOADS & PARAMS (Directly matching NestJS controller DTOs)
// ─────────────────────────────────────────────────────────────

export interface GetTasksParams extends Omit<OptionalPageLimit, 'status'> {
  search?: string;
  priority?: TaskPriority;
  assigneeId?: string; // Filters by specific companyAdmin or Employee id
}

export interface FilterMyCompanyTasksDto
  extends Omit<OptionalPageLimit, 'status'> {
  search?: string;
  priority?: TaskPriority;
  status?: AssignmentStatus[];
  overdueOnly?: boolean; // Maps to transform logical check
  source?: TaskSource; // Filter the unified inbox to one source
  flowType?: TaskFlowType;
  assignmentMode?: TaskAssignmentMode;
  sortBy?: TaskSortBy;
}

export type GetTasksResponse = GetListResponse<TaskItem>;

export interface CreateTaskPayload {
  title: string;
  description?: string;
  priority: TaskPriority;
  requiresApproval?: boolean;
  scheduleType: TaskScheduleType;
  assignmentMode: TaskAssignmentMode;
  assignDate?: string;
  dueDate?: string;
  recurrenceRule?: RecurrenceRuleDto;
  assignees?: TaskAssigneeInput[];
  assignToAllEmployees?: boolean;
  assignToAllAdmins?: boolean;
  // Limits the all-employees / all-admins fan-out to these branches. Omit for
  // the actor's full branch reach.
  branchIds?: string[];
  attachments?: CompanyTaskAttachmentInputDto[];
}

export type UpdateTaskPayload = Partial<CreateTaskPayload>;

export interface ChangeMyStatusDto {
  status: Extract<AssignmentStatus, 'in_progress' | 'submitted' | 'completed'>;
  note?: string;
  attachments?: CompanyTaskAttachmentInputDto[];
}

export interface ApproveAssignmentDto {
  adminNote?: string;
}

export interface BulkApproveAssignmentsDto {
  assignmentIds: string[];
  adminNote?: string;
}

export interface ReopenAssignmentDto {
  adminNote: string; // Required reason text for sending task back
}

export interface CreateMessageDto {
  body: string;
  attachments?: CompanyTaskAttachmentInputDto[];
}

export interface UpdateMessageDto {
  body?: string;
  attachments?: CompanyTaskAttachmentInputDto[];
}

// ─────────────────────────────────────────────────────────────
// DASHBOARD & SUMMARY TYPES
// ─────────────────────────────────────────────────────────────

export interface CompanyTasksDashboardSummary {
  notStarted: number;
  inProgress: number;
  submitted: number;
  approved: number;
  completed: number;
  total: number;
}

export interface TaskSourceCounts {
  pending: number;
  inProgress: number;
  completed: number;
  overdue: number;
}

export interface AdminTasksSummary extends TaskSourceCounts {
  bySource: {
    company: TaskSourceCounts;
    flow: TaskSourceCounts;
  };
  byFlowType: {
    onboarding: TaskSourceCounts;
    offboarding: TaskSourceCounts;
  };
}

export interface TaskFlowEmployeeSummary {
  id: string;
  name: string | null;
}

export interface TaskAssignedBySummary {
  id: string;
  name: string;
  role: string;
}

// Identifies where a task originated — null/null for company tasks.
export interface TaskSourceContext {
  source: TaskSource;
  flowType: TaskFlowType | null;
  flowEmployee: TaskFlowEmployeeSummary | null;
}

export interface MyAdminTask extends TaskSourceContext {
  assignmentId: string;
  taskId: string;
  occurrenceId: string;
  threadId: string | null;

  title: string;
  description: string | null;

  priority: TaskPriority;
  requiresApproval: boolean;
  scheduleType: TaskScheduleType;
  assignmentMode: TaskAssignmentMode;

  assignDate: string;
  dueDate: string | null;

  status: AssignmentStatus;

  submittedAt: string | null;
  completedAt: string | null;
  approvedAt: string | null;

  isOverdue: boolean;
  lastUpdated: string;

  assignedBy: TaskAssignedBySummary;
}

export interface MyAdminTaskDefinition
  extends TaskEntityBase,
    TaskDefinitionCore {
  source: TaskSource;
  dependentTaskId?: string | null;
  unlockedAt?: string | null;
  flowId?: string | null;
  flowEmployeeId?: string | null;
  /** Workflow send steps (D68): completed by signing or confirming. */
  stepKind?: FlowStepKind;
  communication?: FlowStepCommunication | null;
  stepStatus?: FlowStepStatus | null;
}

export interface MyAdminTaskDetail extends TaskSourceContext {
  task: MyAdminTaskDefinition;

  // Null for company tasks — flow tasks freeze (no more self-service
  // uncomplete) once the parent flow itself is marked completed by an admin.
  flowStatus: TaskFlowStatus | null;

  /** A send step's document: the request to sign or the copy to read. */
  stepDocument?: FlowStepDocument | null;

  assignedBy: TaskAssignedBySummary | null;

  occurrence: {
    id: string;
    seq: number;
    assignDate: string;
    dueDate: string | null;
    title: string;
    description: string | null;
    priority: TaskPriority;
    requiresApproval: boolean;
    // Snapshot of the thread shape this occurrence was fanned out with —
    // decide individual vs group from THIS, not `task.assignmentMode`.
    assignmentMode: TaskAssignmentMode | null;
  };

  assignment: {
    id: string;
    occurrenceId: string;
    assigneeUserId: string;
    role: TaskAssigneeType;
    status: AssignmentStatus;

    startedAt: string | null;
    submittedAt: string | null;
    completedAt: string | null;
    approvedAt: string | null;

    approvedBy: string | null;
    adminNote: string | null;
    assigneeNote: string | null;

    createdAt: string;
    updatedAt: string;

    threadId: string | null;

    submissionAttachments: CompanyTaskSubmissionAttachment[];
  } | null;

  threads: CompanyTaskThread[];
}

export type GetMyAdminTaskDetailResponse = ApiResponse<MyAdminTaskDetail>;
